// ** Redux Imports

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logFilter } from "@logging";
import { talkToServer } from "@helpers/servercomms";
import { IDLE, LOADING, SAVING } from "@src/constants";

import defaultSearchSuggestions from "../defaultSearchSuggestions";

const tag = "bookmarksslice";

/* BEGIN BOOKMARK SUPPORT ROUTINTES. 
These are for augmenting data that was stored in the bookmarks b/c some bookmark data is just a set of keys
*/
// export const getDatapointList = createAsyncThunk(
//   "getDatapointList",
//   async (options) => {
//     const request = {
//       context: {
//         topic: "datapoints",
//         action: "getfromlist",
//         options,
//         useCache: false,
//       },
//     };
//     const response = await talkToServer(request);
//     if (response && response !== undefined && response.rc === "OK") {
//       return {
//         response, // pass along what we get back from the server
//       };
//     }
//   }
// );

// BEGIN CORE BOOKMARK ROUTINES
export const saveBookmark = createAsyncThunk(
  "saveBookmark",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "upsert",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const deleteBookmark = createAsyncThunk(
  "deleteBookmark",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "delete",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const saveAutoloadBookmark = createAsyncThunk(
  "saveAutoloadBookmark",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "autoload",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const saveDefaultBookmark = createAsyncThunk(
  "saveDefaultBookmark",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "default",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getDefaultBookmark = createAsyncThunk(
  "getDefaultBookmark",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "getdefault",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getBookmarkByPathAndTitle = createAsyncThunk(
  "getDefaultBookmark",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "getbookmark",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getSuggestionsAsBookmarks = createAsyncThunk(
  "getSuggestionsAsBookmarks",
  async (options) => {
    const request = {
      context: {
        topic: "bookmarks",
        action: "suggestions",
        options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

// END CORE BOOKMARK ROUTINES

export const appDataBookmarksSlice = createSlice({
  name: "bookmarks",
  initialState: {
    // add any default values we might care about here
    bookmarks: {},
    defaultData: {},
    loadedData: {},
    lastError: {},
    suggestions: defaultSearchSuggestions({ isDeveloper: false }),
    status: IDLE,
  },
  reducers: {
    // clear last error
    clearLastError: (state, action) => {
      logFilter("bookmarks", "clearLastError", action.payload);
      state.lastError = null;
    },
    clearBookmarkData: (state, action) => {
      if (state.loadedData[action.payload]) {
        delete state.loadedData[action.payload];
      }
    },
    handlePatchSuggestions: (state, action) => {
      state.suggestions = defaultSearchSuggestions({
        isDeveloper: action.payload.acl.roles.isDeveloper,
        isFieldEngineer: action.payload.acl.roles.isFieldEngineer,
        isFieldAdmin: action.payload.acl.roles.isFieldAdmin,
        isDeviceManagement: action.payload.acl.roles.isDeviceManagement,
        isOperations: action.payload.acl.roles.isOperations,
        isAllBuildings: action.payload.acl.roles.isAllBuildings,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveBookmark.pending, (state) => {
        state.status = SAVING;
      })
      .addCase(saveBookmark.fulfilled, (state, action) => {
        logFilter(tag, "saveBookmark.fulfilled", action);
        state.status = IDLE;
        if (action.payload && action.payload.response.rc === "OK") {
          const sug = state.suggestions;
          sug.map((group) => {
            if (group.groupTitle === "Bookmarks") {
              logFilter(tag, "updating suggestions");
              group.data = action.payload.response.payload.bookmarks;
            }
          });
        }
      })
      .addCase(deleteBookmark.pending, (state) => {
        state.status = SAVING;
      })
      .addCase(deleteBookmark.fulfilled, (state, action) => {
        logFilter(tag, "deleteBookmark.fulfilled", action);
        state.status = IDLE;
        if (action.payload && action.payload.response.rc === "OK") {
          const sug = state.suggestions;
          sug.map((group) => {
            if (group.groupTitle === "Bookmarks") {
              group.data = action.payload.response.payload.bookmarks;
            }
          });
        }
      })
      .addCase(saveAutoloadBookmark.pending, (state) => {
        state.status = SAVING;
      })
      .addCase(saveAutoloadBookmark.fulfilled, (state, action) => {
        logFilter(tag, "saveAutoloadBookmark.fulfilled", action);
        state.status = IDLE;
      })
      .addCase(saveDefaultBookmark.pending, (state) => {
        state.status = SAVING;
      })
      .addCase(saveDefaultBookmark.fulfilled, (state, action) => {
        logFilter(tag, "saveDefaultBookmark.fulfilled", action);
        state.status = IDLE;
      })
      .addCase(getDefaultBookmark.fulfilled, (state, action) => {
        logFilter(tag, "getDefaultBookmark.fulfilled", action);
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            const key = action.payload.response.context.options.pathname;
            state.defaultData[key] = action.payload.response.payload.result;
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No getDefaultBookmark payload to store");
        }
      })
      .addCase(getSuggestionsAsBookmarks.fulfilled, (state, action) => {
        if (action.payload && action.payload.response.rc === "OK") {
          const sug = state.suggestions;
          sug.map((group) => {
            if (group.groupTitle === "Bookmarks") {
              group.data = action.payload.response.payload.bookmarks;
            }
          });
        }
      });
  },
});

export const { clearLastError, clearBookmarkData, handlePatchSuggestions } =
  appDataBookmarksSlice.actions;

export default appDataBookmarksSlice.reducer;
